import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('ENTITY_MOVE');

export const entityMoveCommand: SelfContainedCommand = {
  id: 'entity_move',
  description: `Move any entity to a different folder or to root level.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- Folders (group_*)
- Notes (note_*)
- Sheets (sheet_*)
- Presentations (presentation_*)
- Canvases (canvas_*)
- PDFs (pdf_*)
- Files (file_*, imagefile_*, videofile_*, audiofile_*)
- Book Projects (bookproject_*)
- Any other user-visible entity

**EXAMPLE:**

{ "entityId": "doc_meadow_silver_compass", "targetFolderId": "group_guitar_ocean_zodiac" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',
  
  formatViewData: (result: any, input: any) => {
    // Simple fallback: return undefined for non-success results
    if (!result || result.status !== 'success') {
      return undefined;
    }
    
    const data = result.data || {};
    const entityName = data.entityName || 'Entity';
    const oldLoc = data.oldLocation === 'root' ? 'root' : 'folder';
    const newLoc = data.newLocation === 'root' ? 'root' : 'folder';
    
    return {
      type: 'entity_move',
      summary: result.message || `Moved "${entityName}"`,
      data: {
        entityId: data.entityId,
        entityName: entityName,
        oldLocation: data.oldLocation,
        newLocation: data.newLocation,
        direction: `${oldLoc} → ${newLoc}`
      }
    };
  },
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'ID of the entity to move (any visible entity: doc_*, group_*, note_*, sheet_*, pdf_*, file_*, canvas_*, presentation_*, etc.)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Entity ID cannot be empty';
          return null;
        },
        examples: [
          'doc_meadow_silver_compass',
          'group_cascade_thunder_phoenix',
          'note_bright_sun_cool_moon',
          'sheet_budget_2026',
          'pdf_red_mountain_deep_river'
        ]
      },
      {
        name: 'targetFolderId',
        type: 'string',
        description: 'ID of destination folder (group_*), or omit to move to root. Do not pass null or empty string.',
        required: false,
        validator: (value: any) => {
          // Allow undefined (parameter not provided) - this means move to root
          if (value === undefined) return null;
          
          // Reject null, empty string, or non-string values
          if (value === null) return 'To move to root, omit targetFolderId';
          if (typeof value !== 'string') return 'Folder ID must be a string, or omit targetFolderId to move to root';
          if (value.trim().length === 0) return 'Folder ID cannot be empty - omit targetFolderId to move to root';
          
          // Reject string "null" or "undefined" (common serialization mistake)
          if (value === 'null' || value === 'undefined') return 'Do not pass string "null" - omit targetFolderId to move to root';
          
          return null;
        },
        examples: [
          'group_guitar_ocean_zodiac',
          'group_meadow_silver_compass'
          // To move to root: omit targetFolderId
        ]
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { entityId: 'doc_wild_mountain_calm_river', targetFolderId: 'group_bright_sun_cool_moon' },
        expectedOutput: createSuccessResult('Moved "Research Paper" to folder', {
          entityId: 'doc_wild_mountain_calm_river',
          oldLocation: 'root',
          newLocation: 'group_bright_sun_cool_moon'
        }),
        description: 'Move a document from root into a folder',
        context: 'When organizing loose documents into project folders'
      },
      {
        input: { entityId: 'doc_calm_river_wise_forest' },  // ← targetFolderId omitted
        expectedOutput: createSuccessResult('Moved "Meeting Notes" to root', {
          entityId: 'doc_calm_river_wise_forest',
          oldLocation: 'group_project_alpha',
          newLocation: 'root'
        }),
        description: 'Move a document to root (omit targetFolderId)',
        context: 'When moving documents to top level, not inside any folder'
      },
      {
        input: { entityId: 'group_nested_folder', targetFolderId: 'group_parent_folder' },
        expectedOutput: createSuccessResult('Moved "Nested Folder" to folder', {
          entityId: 'group_nested_folder',
          oldLocation: 'root',
          newLocation: 'group_parent_folder'
        }),
        description: 'Move a folder into another folder (nested folders)',
        context: 'When creating hierarchical folder structures'
      },
      {
        input: { entityId: 'note_bright_sun_cool_moon', targetFolderId: 'group_research_papers' },
        expectedOutput: createSuccessResult('Moved "Quick Note" to folder', {
          entityId: 'note_bright_sun_cool_moon',
          oldLocation: 'root',
          newLocation: 'group_research_papers'
        }),
        description: 'Move a note into a folder',
        context: 'Works the same as moving documents — entity_move supports all entity types'
      },
      {
        input: { entityId: 'pdf_red_mountain_deep_river', targetFolderId: 'group_archive' },
        expectedOutput: createSuccessResult('Moved "Annual Report.pdf" to folder', {
          entityId: 'pdf_red_mountain_deep_river',
          oldLocation: 'root',
          newLocation: 'group_archive'
        }),
        description: 'Move a PDF into a folder',
        context: 'Works for any file type: pdf_*, file_*, imagefile_*, sheet_*, canvas_*, etc.'
      }
    ],
    bad: [
      {
        input: { entityId: '', targetFolderId: 'group_abc' },
        error: 'Entity ID cannot be empty',
        why: 'The validator checks for empty or whitespace-only entity IDs',
        fix: 'Provide a valid entity ID like "doc_wild_mountain_calm_river"'
      },
      {
        input: { entityId: 'doc_abc', targetFolderId: '' },
        error: 'Folder ID cannot be empty - omit targetFolderId to move to root',
        why: 'Empty strings are rejected - to move to root, omit targetFolderId',
        fix: 'To move to root: { entityId: "doc_abc" }. To move to folder: { entityId: "doc_abc", targetFolderId: "group_xyz" }'
      },
      {
        input: { entityId: 'doc_abc', targetFolderId: 'null' },
        error: 'Do not pass string "null" - omit targetFolderId to move to root',
        why: 'String "null" is not the same as omitting targetFolderId',
        fix: 'To move to root, omit targetFolderId: { entityId: "doc_abc" }'
      },
      {
        input: { entityId: 'nonexistent_id_123', targetFolderId: 'group_abc' },
        error: 'Entity not found',
        why: 'The entity ID does not exist in the workspace',
        fix: 'Use folder_list to see available entities, then reference their actual IDs'
      }
    ]
  },
  
  performance: {
    expectedDuration: 400,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    let { entityId, targetFolderId } = params;
    
    // Critical: Validate entityId before using it
    if (!entityId || typeof entityId !== 'string') {
      return createErrorResult(
        'Entity ID is required',
        'entityId parameter is required and must be a string',
        'validation',
        { data: { entityId } }
      );
    }
    
    try {
      // ✅ Resolve llmId to _id if needed (for both entityId and targetFolderId)
      const entityResolution = resolveEntityId(entityId);
      
      if (entityResolution.isLlmId && !entityResolution.entity) {
        return createErrorResult(
          'Entity not found',
          `No entity found with llmId: ${entityId}`,
          'validation',
          { data: { providedId: entityId } }
        );
      }
      
      entityId = entityResolution.resolved;
      
      // Also resolve targetFolderId if it's provided
      if (targetFolderId) {
        const folderResolution = resolveEntityId(targetFolderId);
        if (folderResolution.isLlmId && !folderResolution.entity) {
          return createErrorResult(
            'Target folder not found',
            `No folder found with llmId: ${targetFolderId}`,
            'validation',
            { data: { providedId: targetFolderId } }
          );
        }
        targetFolderId = folderResolution.resolved;
      }
      
      log.info('[entity_move] Moving entity:', { 
        originalEntityId: params.entityId,
        resolvedEntityId: entityId,
        originalTargetId: params.targetFolderId,
        resolvedTargetId: targetFolderId,
        wasEntityLlmId: entityResolution.isLlmId
      });
      
      const entityStore = useEntityStore.getState();
      const entity = entityStore.entities.find(e => e._id === entityId);
      
      if (!entity) {
        return createErrorResult(
          'Entity not found',
          `Entity with ID ${entityId} does not exist`,
          'validation',
          { data: { entityId } }
        );
      }
      
      const entityParentId = (entity as any).parentId as string | undefined;
      const currentParent = entityParentId
        ? entityStore.entities.find(e => e._id === entityParentId)
        : undefined;
      
      await entityStore.actions.moveEntity(entityId, targetFolderId || null);
      
      const result = {
        entityId,
        entityName: entity.name,
        oldLocation: currentParent?._id || 'root',
        newLocation: targetFolderId || 'root',
        moved: true
      };
      
      log.info('[entity_move] ✅ Entity moved successfully:', result);
      
      return createSuccessResult(
        `Moved "${entity.name}" to ${targetFolderId ? 'folder' : 'root'}`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore.actions',
            affectedEntities: [entityId, ...(currentParent ? [currentParent._id] : []), ...(targetFolderId ? [targetFolderId] : [])]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_move] Error:', error);
      return createErrorResult(
        'Failed to move entity',
        error.message || 'Unknown error',
        'execution',
        {
          data: { entityId, targetFolderId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

